import { Offer } from '@appTypes/essential-ui/Offer';
import { useSelector } from 'react-redux';
import { DealType } from '../../enums/essential-ui/EssentialDealType';
import { VehicleType } from '../../enums/essential-ui/EssentialVehicleType';
import { OfferType } from '../../enums/essential-ui/OfferType';
import { SortBy } from '../../enums/essential-ui/SortBy';
import { AppState } from '../../store/AppState';
import { FiltersState } from '../../store/essential-ui/filters/FiltersSlice';
import { OffersState } from '../../store/essential-ui/offers/OffersSlice';
import { CampaignActivityFilter } from '../../types/CampaignActivityFilter';
import { MinMaxFilter } from '../../types/essential-ui/MinMaxFilter';

export const useFilteredOffers = (): UseFilteredOffersReturnType => {
  const { offers, loading, error, currentPage, keepUsersOnSamePage, currentPageSize } = useSelector<
    AppState,
    OffersState
  >((state) => state.offers);
  const filters = useSelector<AppState, FiltersState>((state) => state.filters);
  const filteredOffers: readonly Offer[] = applyFilters(offers, filters);
  const sortedOffers: readonly Offer[] = sortOffers(filteredOffers, filters.sortBy);
  const count: number = sortedOffers.length;

  return {
    offers: sortedOffers,
    unfilteredOffers: offers,
    count,
    error,
    loading,
    currentPage,
    keepUsersOnSamePage,
    currentPageSize,
  };
};

export const applyFilters = (offers: readonly Offer[], filters: FiltersState): readonly Offer[] => {
  let filteredOffers = offers
    .filter((offer) => filterOffersWithGivenDealTypesOnly(offer, filters.dealTypes))
    .filter((offer) =>
      filterOffersWithGivenCampaignActivityState(offer, filters.campaignActivitiesFilter),
    )
    .filter((offer) => filterOffersWithinGivenBPSRange(offer, filters.bps))
    .filter((offer) =>
      filterOffersWithinGivenRemainingPaymentsRange(offer, filters.remainingPayments),
    )
    .filter((offer) => filterOffersWithinGivenLastUpdateRange(offer, filters.lastUpdate))
    .filter((offer) => filterOffersWithGivenIncentivesOnly(offer, filters.incentives))
    .filter((offer) => filterOffersWithGivenPrivateIncentivesOnly(offer, filters.privateIncentives))
    .filter((offer) => filterOffersWithGivenYearsOnly(offer, filters.years))
    .filter((offer) => filterOffersWithGivenModelsOnly(offer, filters.models))
    .filter((offer) => filterOffersWithGivenSoldByOnly(offer, filters.soldByList))
    .filter((offer) => filterOffersWithGivenTrimsOnly(offer, filters.trims))
    .filter((offer) => filterOffersWithGivenVehicleTypesOnly(offer, filters.vehicleTypes))
    .filter((offer) => filterOffersWithGivenBrandsOnly(offer, filters.brands));

  switch (filters.offerType) {
    case OfferType.NinetyDaysOnLeaseContract:
      filteredOffers = filteredOffers.filter(
        (offer) =>
          offer.engagementModel.campaignReceived && offer.hasLessThanNinetyDaysOnLeaseContract,
      );
      break;
    case OfferType.CampaignViews:
      filteredOffers = filteredOffers.filter((offer) => offer.engagementModel.timesOpened > 0);
      break;
    case OfferType.CampaignViewsMso:
      filteredOffers = filteredOffers.filter((offer) => offer.engagementModel.timesOpened > 0);
      break;
    case OfferType.ExpiringSoon:
      filteredOffers = filteredOffers.filter((offer) => !!offer.expiring);
      break;
    case OfferType.LastUpdated:
      filteredOffers = filteredOffers.filter(
        (offer) => offer.engagementModel.campaignReceived && offer.daysSinceLastUpdate > 60,
      );
      break;
    case OfferType.AllDeals:
    default:
      filteredOffers = filteredOffers;
  }

  // console.log('filteredOffers', filteredOffers);
  return filteredOffers;
};

const sortOffers = (offers: readonly Offer[], sortBy: SortBy): readonly Offer[] => {
  switch (sortBy) {
    case SortBy.BPS_HIGHEST:
      return [...offers].sort((a, b) => b.bpsScore - a.bpsScore);
    case SortBy.BPS_LOWEST:
      return [...offers].sort((a, b) => a.bpsScore - b.bpsScore);
    case SortBy.LAST_UPDATED_ASC:
      return [...offers].sort((a, b) => a.daysSinceLastUpdate - b.daysSinceLastUpdate);
    case SortBy.LAST_UPDATED_DESC:
      return [...offers].sort((a, b) => b.daysSinceLastUpdate - a.daysSinceLastUpdate);
    case SortBy.DEAL_TYPE_ASC:
      return [...offers].sort((a, b) => a.dealType.localeCompare(b.dealType));
    case SortBy.DEAL_TYPE_DESC:
      return [...offers].sort((a, b) => b.dealType.localeCompare(a.dealType));
    case SortBy.CUSTOMER_ASC:
      return [...offers].sort((a, b) => a.fullName.localeCompare(b.fullName));
    case SortBy.CUSTOMER_DESC:
      return [...offers].sort((a, b) => b.fullName.localeCompare(a.fullName));
    case SortBy.OFFER_ASC:
      return [...offers].sort((a, b) => parseInt(a.discount) - parseInt(b.discount));
    case SortBy.OFFER_DESC:
      return [...offers].sort((a, b) => parseInt(b.discount) - parseInt(a.discount));
    case SortBy.EXPIRING_IN_ASC:
      return [...offers].sort((a, b) => a.numberOfDaysOfferExpiring - b.numberOfDaysOfferExpiring);
    case SortBy.EXPIRING_IN_DESC:
      return [...offers].sort((a, b) => b.numberOfDaysOfferExpiring - a.numberOfDaysOfferExpiring);
    default:
      return [...offers].sort((a, b) => b.bpsScore - a.bpsScore);
  }
};

const filterOffersWithGivenDealTypesOnly = (offer: Offer, types: readonly DealType[]) =>
  types.includes(offer.dealType as DealType);

const filterOffersWithGivenVehicleTypesOnly = (offer: Offer, types: readonly VehicleType[]) => {
  if (types.includes(VehicleType.New) && offer.isNew) {
    return true;
  }

  if (types.includes(VehicleType.PreOwned) && !offer.isNew) {
    return true;
  }

  return false;
};

const filterOffersWithGivenCampaignActivityState = (
  offer: Offer,
  campaignActivity: CampaignActivityFilter,
) => {
  if (campaignActivity.zeroTimesOpened && offer.engagementModel.timesOpened === 0) {
    return true;
  }
  if (campaignActivity.oneTimeOpened && offer.engagementModel.timesOpened === 1) {
    return true;
  }
  if (campaignActivity.twoOrMoreTimesOpened && offer.engagementModel.timesOpened >= 2) {
    return true;
  }

  return false;
};

const filterOffersWithinGivenBPSRange = (offer: Offer, bps: MinMaxFilter) =>
  offer.bpsScore >= bps.min && offer.bpsScore <= bps.max;

const filterOffersWithinGivenRemainingPaymentsRange = (
  offer: Offer,
  remainingPayments: MinMaxFilter,
) =>
  offer.numberOfPaymentsRemaining >= remainingPayments.min &&
  offer.numberOfPaymentsRemaining <= remainingPayments.max;

const filterOffersWithinGivenLastUpdateRange = (offer: Offer, lastUpdate: MinMaxFilter) =>
  offer.daysSinceLastUpdate >= lastUpdate.min && offer.daysSinceLastUpdate <= lastUpdate.max;

const filterOffersWithGivenYearsOnly = (offer: Offer, years: readonly number[]) =>
  years.includes(offer.year);

const filterOffersWithGivenIncentivesOnly = (offer: Offer, incentivesFilter: readonly string[]) => {
  if (incentivesFilter.includes('N/A') && offer.incentiveNames.length === 0) {
    return true;
  }
  return incentivesFilter.some((elem) => offer.incentiveNames.includes(elem));
};

const filterOffersWithGivenPrivateIncentivesOnly = (
  offer: Offer,
  privateIncentivesFilter: readonly string[],
) => {
  return (
    privateIncentivesFilter.includes(offer.incentiveExpiryDate) ||
    offer.incentiveExpiryDate === null
  );
};

const filterOffersWithGivenModelsOnly = (offer: Offer, modelsFilter: readonly string[]) =>
  modelsFilter.some(() => modelsFilter.includes(offer.model));

const filterOffersWithGivenTrimsOnly = (offer: Offer, trimsFilter: readonly string[]) =>
  trimsFilter.some(() => trimsFilter.includes(offer.trim));

const filterOffersWithGivenSoldByOnly = (offer: Offer, soldByFilter: readonly string[]) =>
  soldByFilter.some(
    () => offer.originalSalesPersonName && soldByFilter.includes(offer.originalSalesPersonName),
  );

const filterOffersWithGivenBrandsOnly = (offer: Offer, brandsFilter: readonly string[]) =>
  brandsFilter.some(() => offer.brand && brandsFilter.includes(offer.brand));

export type UseFilteredOffersReturnType = OffersState & {
  count: number;
  unfilteredOffers: readonly Offer[];
};
